import { Category, CategoryWithSubs, Transaction } from '../store/useFinanceStore';

export const GOAL_CATEGORY_FALLBACK = {
  id: 'goal-contribution',
  name: 'Aporte da Meta',
  icon: '💠',
  color: '#6366f1',
} as const;

export const GOAL_SUBCATEGORY_ICON = '🎯';
const DEFAULT_GOAL_NAME = 'Meta não identificada';

const normalizeText = (value?: string | null) =>
  value ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';

const GOAL_CONTRIBUTION_KEYWORDS = [
  'aporte da meta',
  'deposito para meta',
  'deposito adicional para meta',
  'meta criada',
  'contribuicao',
];

const GOAL_REFUND_KEYWORDS = [
  'reembolso de meta',
  'reembolso da meta',
  'reembolso meta',
  'meta cancelada',
  'cancelamento de meta',
  'estorno de meta',
];

export type GoalMovementKind = 'contribution' | 'refund';

export interface GoalDescriptor {
  key: string;
  name: string;
}

const sanitizeGoalKey = (value: string) => {
  const normalized = normalizeText(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || null;
};

const extractAfterColon = (text: string) => {
  const colonIndex = text.indexOf(':');
  return colonIndex === -1 ? text.trim() : text.slice(colonIndex + 1).trim();
};

const extractBetweenColonAndHyphen = (text: string) => {
  const colonIndex = text.indexOf(':');
  if (colonIndex === -1) return text.trim();
  const rest = text.slice(colonIndex + 1).trim();
  const hyphenIndex = rest.indexOf('-');
  return (hyphenIndex === -1 ? rest : rest.slice(0, hyphenIndex)).trim();
};

const cleanGoalName = (value: string) =>
  value
    .replace(/^["'\s]+/, '')
    .replace(/["'\s]+$/, '')
    .trim();

export const extractGoalDescriptor = (
  transaction: Pick<Transaction, 'id' | 'description' | 'notes'>
): GoalDescriptor => {
  const description = transaction.description ?? '';
  const normalizedDescription = normalizeText(description);

  const extractionCandidates: Array<() => string | null> = [
    () => {
      if (normalizedDescription.startsWith('deposito adicional para meta')) {
        return extractAfterColon(description);
      }
      return null;
    },
    () => {
      if (normalizedDescription.startsWith('deposito para meta')) {
        return extractAfterColon(description);
      }
      return null;
    },
    () => {
      if (normalizedDescription.startsWith('meta:')) {
        return extractBetweenColonAndHyphen(description);
      }
      return null;
    },
    () => {
      if (normalizedDescription.startsWith('atualizacao manual')) {
        return extractAfterColon(description);
      }
      return null;
    },
    () => {
      if (normalizedDescription.startsWith('reembolso de meta cancelada')) {
        return extractAfterColon(description);
      }
      return null;
    },
    () => {
      if (normalizedDescription.startsWith('reembolso de meta')) {
        return extractAfterColon(description);
      }
      return null;
    },
    () => {
      if (normalizedDescription.startsWith('reembolso da meta')) {
        return extractAfterColon(description);
      }
      return null;
    },
    () => {
      if (normalizedDescription.startsWith('meta cancelada')) {
        return extractAfterColon(description);
      }
      return null;
    },
  ];

  let extractedName = '';

  for (const extractor of extractionCandidates) {
    const result = extractor();
    if (result) {
      extractedName = result;
      break;
    }
  }

  if (!extractedName && transaction.notes) {
    extractedName = transaction.notes;
  }

  const displayName = cleanGoalName(extractedName || DEFAULT_GOAL_NAME);
  const keySource = extractedName || normalizedDescription || transaction.id || DEFAULT_GOAL_NAME;
  const sanitizedKey = sanitizeGoalKey(keySource) ?? `goal-${transaction.id ?? 'desconhecida'}`;

  return {
    key: sanitizedKey,
    name: displayName || DEFAULT_GOAL_NAME,
  };
};

export const resolveGoalCategoryContext = (categoriesWithSubs: CategoryWithSubs[]) => {
  const goalCategoryIds = new Set<string>([GOAL_CATEGORY_FALLBACK.id]);
  let goalCategory: Category | null = null;

  categoriesWithSubs.forEach((category) => {
    if (normalizeText(category.name) === 'aporte da meta') {
      goalCategoryIds.add(category.id);
      if (!goalCategory) {
        goalCategory = category;
      }
    }

    category.subcategories.forEach((sub) => {
      if (normalizeText(sub.name) === 'aporte da meta') {
        goalCategoryIds.add(sub.id);
        if (!goalCategory) {
          goalCategory = sub;
        }
      }
    });
  });

  return {
    goalCategory,
    goalCategoryIds,
  };
};

export const classifyGoalMovement = (
  transaction: Pick<Transaction, 'amount' | 'type' | 'category' | 'category_id' | 'description'>,
  goalCategoryIds: Set<string>
): GoalMovementKind | null => {
  const normalizedCategory = normalizeText(transaction.category);
  const normalizedDescription = normalizeText(transaction.description);
  const isExpense = transaction.amount < 0 || transaction.type === 'expense';
  const isIncome = transaction.amount > 0 || transaction.type === 'income';
  const hasGoalCategory = Boolean(
    transaction.category_id && goalCategoryIds.has(transaction.category_id)
  ) || normalizedCategory === 'aporte da meta';

  const matchesContributionKeyword = GOAL_CONTRIBUTION_KEYWORDS.some((keyword) =>
    normalizedDescription.includes(keyword)
  );
  const matchesRefundKeyword = GOAL_REFUND_KEYWORDS.some((keyword) =>
    normalizedDescription.includes(keyword)
  );

  if (isExpense && (hasGoalCategory || matchesContributionKeyword)) {
    return 'contribution';
  }

  if (isIncome && (hasGoalCategory || matchesRefundKeyword)) {
    return 'refund';
  }

  if (matchesRefundKeyword) return 'refund';
  if (matchesContributionKeyword) return 'contribution';

  return null;
};
